import { useMemo, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  FormSection, Input, RadixSelect, StatusBadge,
} from '@/components/ui';
import { CategorySearchSelect } from './CategorySearchSelect';
import type {
  BulkUpdateInput,
  DisplayProductStatus,
  ProductBulkResult,
} from '../hooks/useProductBulkMutation';
import type { BulkHomeSectionChanges } from '../hooks/useBulkHomeSections';

/** QA-589 메인 섹션 한 줄(사용 중 섹션, 메인 노출 순서). */
export interface BulkHomeSectionOption {
  sectionKey: string;
  label: string;
}

const LIMIT_BASIS = [
  { value: 'OPTION', label: '옵션 기준' },
  { value: 'PRODUCT', label: '상품 기준' },
  { value: 'MEMBER', label: '아이디 기준' },
] as const;

export interface ProductBulkUpdateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  busy?: boolean;
  /** 직전 실행 결과 — 닫을 때까지 유지해 성공/스킵/실패 집계를 표시. */
  result?: ProductBulkResult | null;
  /** QA-589 메인 섹션 목록(상위가 읽어 넘긴다). 비면 섹션 칸을 숨긴다. */
  homeSections?: ReadonlyArray<BulkHomeSectionOption>;
  /** 변경 필드만 담은 부분 입력을 상위로 — productIds 는 상위가 채운다. sectionChanges 는 메인 섹션 넣기/빼기. */
  onSubmit: (changes: Omit<BulkUpdateInput, 'productIds'>, sectionChanges: BulkHomeSectionChanges) => void;
}

// "미변경" sentinel — RadixSelect 빈 문자열 값. 상태/진열은 부분 시맨틱이라 미선택=미변경.
const UNCHANGED = '';
const STATUS_VALUES: ReadonlyArray<DisplayProductStatus> = ['ON_SALE', 'SALE_ENDED', 'HIDDEN', 'PREPARING'];

const parseAmount = (raw: string): number | null => {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t.replace(/,/g, ''));
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
};

/**
 * 15-B-3 (ID90) — 상품 일괄 변경 모달. 선택한 상품들의 공통 속성(판매상태/진열/대표카테고리/정가/판매가)을
 * 부분 시맨틱(미선택=미변경)으로 한 번에 변경한다. 적어도 한 필드를 바꿔야 [적용]이 활성된다.
 * 가격은 둘 다 입력 시 판매가 ≤ 정가 를 프런트에서도 1차 검증(BE 가 최종 검증).
 */
export const ProductBulkUpdateModal = ({
  open,
  onOpenChange,
  selectedCount,
  busy,
  result,
  homeSections = [],
  onSubmit,
}: ProductBulkUpdateModalProps) => {
  const { t } = useTranslation('products');
  const [status, setStatus] = useState<string>(UNCHANGED);
  const [displayed, setDisplayed] = useState<string>(UNCHANGED); // '', 'true', 'false'
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [listPrice, setListPrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [error, setError] = useState<string | null>(null);
  // QA-589 메인 섹션 노출 — 섹션마다 변경 안 함 / 노출 / 노출 해제.
  const [sectionChanges, setSectionChanges] = useState<BulkHomeSectionChanges>({});
  // QA-589 1인 구매 수량 제한 — 바꿀 때만.
  const [limitOn, setLimitOn] = useState(false);
  const [limitBasis, setLimitBasis] = useState<'OPTION' | 'PRODUCT' | 'MEMBER'>('OPTION');
  const [limitMin, setLimitMin] = useState('');
  const [limitMax, setLimitMax] = useState('');

  const statusOptions = useMemo(
    () => [
      { value: UNCHANGED, label: t('products.bulkUpdate.unchanged', { defaultValue: '변경 안 함' }) },
      ...STATUS_VALUES.map((s) => ({ value: s, label: t(`products.saleStatus.${s}`) })),
    ],
    [t],
  );
  const displayedOptions = useMemo(
    () => [
      { value: UNCHANGED, label: t('products.bulkUpdate.unchanged', { defaultValue: '변경 안 함' }) },
      { value: 'true', label: t('products.bulkUpdate.displayedShown', { defaultValue: '진열' }) },
      { value: 'false', label: t('products.bulkUpdate.displayedHidden', { defaultValue: '숨김' }) },
    ],
    [t],
  );

  const listAmount = parseAmount(listPrice);
  const saleAmount = parseAmount(salePrice);
  const limitMinN = parseAmount(limitMin);
  const limitMaxN = parseAmount(limitMax);

  const changes = useMemo<Omit<BulkUpdateInput, 'productIds'>>(() => {
    const out: Omit<BulkUpdateInput, 'productIds'> = {};
    if (status !== UNCHANGED) out.status = status as DisplayProductStatus;
    if (displayed !== UNCHANGED) out.displayed = displayed === 'true';
    if (categoryId) out.primaryCategoryId = categoryId;
    if (listAmount != null) out.listPrice = { amount: listAmount, currencyCode: 'KRW' };
    if (saleAmount != null) out.salePrice = { amount: saleAmount, currencyCode: 'KRW' };
    if (limitOn) out.purchaseLimit = { basis: limitBasis, min: limitMinN, max: limitMaxN };
    return out;
  }, [status, displayed, categoryId, listAmount, saleAmount, limitOn, limitBasis, limitMinN, limitMaxN]);

  const hasAnyChange = Object.keys(changes).length > 0 || Object.keys(sectionChanges).length > 0;

  const handleSubmit = () => {
    setError(null);
    if (!hasAnyChange) {
      setError(t('products.bulkUpdate.noChange', { defaultValue: '변경할 항목을 하나 이상 선택하세요.' }));
      return;
    }
    if (listAmount != null && saleAmount != null && saleAmount > listAmount) {
      setError(t('products.bulkUpdate.priceCoherence', { defaultValue: '판매가는 정가보다 클 수 없습니다.' }));
      return;
    }
    if (limitOn && ((limitMinN != null && limitMinN < 1) || (limitMaxN != null && limitMaxN < 1) || (limitMinN != null && limitMaxN != null && limitMinN > limitMaxN))) {
      setError('구매 수량은 1 이상이고, 최소 수량은 최대 수량보다 클 수 없습니다.');
      return;
    }
    onSubmit(changes, sectionChanges);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onOpenChange(false); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('products.bulkUpdate.title', { defaultValue: '상품 일괄 변경' })}</DialogTitle>
          <DialogDescription>
            {t('products.bulkUpdate.desc', {
              count: selectedCount,
              defaultValue: `선택한 ${selectedCount}건의 공통 속성을 일괄 변경합니다. 비워 둔 항목은 변경되지 않습니다.`,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-1">
          <FormSection label={t('products.bulkUpdate.status', { defaultValue: '판매 상태' })} density="compact">
            <RadixSelect
              id="bulk-status"
              value={status}
              onChange={setStatus}
              options={statusOptions}
            />
          </FormSection>
          <FormSection label={t('products.bulkUpdate.displayed', { defaultValue: '진열 여부' })} density="compact">
            <RadixSelect
              id="bulk-displayed"
              value={displayed}
              onChange={setDisplayed}
              options={displayedOptions}
            />
          </FormSection>
          <FormSection
            label={t('products.bulkUpdate.category', { defaultValue: '대표 카테고리' })}
            density="compact"
            hint={t('products.bulkUpdate.categoryHint', { defaultValue: '선택하지 않으면 카테고리는 변경되지 않습니다.' })}
          >
            <CategorySearchSelect
              value={categoryId}
              onChange={setCategoryId}
              placeholder={t('products.bulkUpdate.categoryUnchanged', { defaultValue: '변경 안 함' })}
              testId="bulk-category"
            />
          </FormSection>
          <div className="grid grid-cols-2 gap-3">
            <FormSection label={t('products.bulkUpdate.listPrice', { defaultValue: '정가 (KRW)' })} density="compact">
              <Input
                inputMode="numeric"
                value={listPrice}
                onChange={(e) => setListPrice(e.target.value)}
                placeholder={t('products.bulkUpdate.pricePlaceholder', { defaultValue: '변경 안 함' })}
                aria-label={t('products.bulkUpdate.listPrice', { defaultValue: '정가 (KRW)' })}
              />
            </FormSection>
            <FormSection label={t('products.bulkUpdate.salePrice', { defaultValue: '판매가 (KRW)' })} density="compact">
              <Input
                inputMode="numeric"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                placeholder={t('products.bulkUpdate.pricePlaceholder', { defaultValue: '변경 안 함' })}
                aria-label={t('products.bulkUpdate.salePrice', { defaultValue: '판매가 (KRW)' })}
              />
            </FormSection>
          </div>

          {/* QA-589 메인 섹션 노출 — 상품 수정의 [메인 섹션 노출]과 같다(노출 = 섹션 맨 앞에 넣기). */}
          {homeSections.length > 0 ? (
            <FormSection label="메인 섹션 노출" density="compact" hint="노출하면 섹션 맨 앞에 넣습니다. 순서는 메인 페이지 관리에서 바꿉니다.">
              <div className="flex flex-col gap-1.5" data-testid="bulk-home-sections">
                {homeSections.map((s) => (
                  <label key={s.sectionKey} className="flex items-center justify-between gap-3 text-[13px]">
                    <span>
                      {s.label} <span className="text-[color:var(--content-muted)]">({s.sectionKey})</span>
                    </span>
                    <select
                      className="h-8 rounded-md border px-2 text-[13px]"
                      style={{ borderColor: 'var(--divider-strong)', background: 'var(--surface-0)' }}
                      value={sectionChanges[s.sectionKey] ?? ''}
                      onChange={(e) =>
                        setSectionChanges((prev) => {
                          const next = { ...prev };
                          if (e.target.value === 'ADD' || e.target.value === 'REMOVE') next[s.sectionKey] = e.target.value;
                          else delete next[s.sectionKey];
                          return next;
                        })
                      }
                      aria-label={`${s.label} 노출`}
                      data-testid={`bulk-home-section-${s.sectionKey}`}
                    >
                      <option value="">변경 안 함</option>
                      <option value="ADD">노출</option>
                      <option value="REMOVE">노출 해제</option>
                    </select>
                  </label>
                ))}
              </div>
            </FormSection>
          ) : null}

          {/* QA-589 1인 구매 수량 제한 — 상품 수정 [1인 구매 수량 제한]과 같은 값. 비우면 제한 없음. */}
          <FormSection label="1인 구매 수량 제한" density="compact">
            <div className="flex flex-col gap-2" data-testid="bulk-purchase-limit">
              <label className="inline-flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={limitOn} onChange={(e) => setLimitOn(e.target.checked)} data-testid="bulk-purchase-limit-on" />
                변경하기
              </label>
              {limitOn ? (
                <>
                  <div className="flex flex-wrap gap-3 text-[13px]">
                    {LIMIT_BASIS.map((b) => (
                      <label key={b.value} className="inline-flex items-center gap-1.5">
                        <input type="radio" name="bulk-limit-basis" checked={limitBasis === b.value} onChange={() => setLimitBasis(b.value)} />
                        {b.label}
                      </label>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input inputMode="numeric" value={limitMin} onChange={(e) => setLimitMin(e.target.value)} placeholder="최소 (비우면 제한 없음)" aria-label="최소 구매 수량" />
                    <Input inputMode="numeric" value={limitMax} onChange={(e) => setLimitMax(e.target.value)} placeholder="최대 (비우면 제한 없음)" aria-label="최대 구매 수량" />
                  </div>
                </>
              ) : null}
            </div>
          </FormSection>

          {error ? (
            <p role="alert" className="text-xs" style={{ color: 'var(--status-danger-fg)' }}>
              {error}
            </p>
          ) : null}

          {result ? (
            <div
              className="flex flex-wrap items-center gap-2 rounded-md px-3 py-2 text-xs"
              style={{ background: 'var(--surface-1)' }}
              data-testid="bulk-update-result"
            >
              <StatusBadge variant="success" label={t('products.bulkUpdate.resultSucceeded', { count: result.succeeded, defaultValue: `성공 ${result.succeeded}` })} />
              {result.skipped > 0 ? (
                <StatusBadge variant="info" label={t('products.bulkUpdate.resultSkipped', { count: result.skipped, defaultValue: `스킵 ${result.skipped}` })} />
              ) : null}
              {result.failed.length > 0 ? (
                <StatusBadge variant="warning" label={t('products.bulkUpdate.resultFailed', { count: result.failed.length, defaultValue: `실패 ${result.failed.length}` })} />
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm" disabled={busy}>
              {t('products.bulkUpdate.cancel', { defaultValue: '취소' })}
            </Button>
          </DialogClose>
          <Button
            size="sm"
            disabled={busy || !hasAnyChange}
            onClick={handleSubmit}
            data-testid="bulk-update-submit"
          >
            {busy
              ? t('products.bulkUpdate.applying', { defaultValue: '적용 중…' })
              : t('products.bulkUpdate.apply', { defaultValue: '일괄 변경 적용' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
